import { useState, useCallback, useMemo } from 'react';
import { VisibilityState, ColumnDef } from '@tanstack/react-table';

declare module '@tanstack/react-table' {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends unknown, TValue> {
    defaultHidden?: boolean;
  }
}

const STORAGE_PREFIX = 'workflow-admin-columns-';

export function getDefaultVisibility<TData>(columns: ColumnDef<TData, any>[]): VisibilityState {
  const defaults: VisibilityState = {};
  for (const col of columns) {
    const id = (col as any).accessorKey || (col as any).id;
    if (id && (col as any).meta?.defaultHidden) {
      defaults[id] = false;
    }
  }
  return defaults;
}

export function useColumnVisibility(pageKey: string, defaultVisibility: VisibilityState) {
  const storageKey = STORAGE_PREFIX + pageKey;

  const [columnVisibility, setColumnVisibilityState] = useState<VisibilityState>(() => {
    try {
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved) as VisibilityState;
        // Merge: saved values take precedence, but new columns from defaults get included
        return { ...defaultVisibility, ...parsed };
      }
    } catch {
      // ignore parse errors
    }
    return defaultVisibility;
  });

  const setColumnVisibility = useCallback((updater: VisibilityState | ((prev: VisibilityState) => VisibilityState)) => {
    setColumnVisibilityState(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try {
        localStorage.setItem(storageKey, JSON.stringify(next));
      } catch {
        // ignore storage errors
      }
      return next;
    });
  }, [storageKey]);

  const resetToDefaults = useCallback(() => {
    localStorage.removeItem(storageKey);
    setColumnVisibilityState(defaultVisibility);
  }, [storageKey, defaultVisibility]);

  return useMemo(() => ({
    columnVisibility,
    setColumnVisibility,
    resetToDefaults,
  }), [columnVisibility, setColumnVisibility, resetToDefaults]);
}
